/** Stage pill labels for client rows (Pulse journey stage strings). */

import type { ClientStageGroup } from "@/services/api/agentic-v2/types";

const STAGE_GROUP_CONTAINS: Record<ClientStageGroup, readonly string[]> = {
  in_dg: ["send tob", "tob with client", "awaiting dg", "in dg", "ani tech"],
  in_paraplanning: ["in paraplanning", "11."],
  in_advice: ["in advice", "12."],
};

/** Strip the numeric Airtable prefix from a journey stage label. */
export function shortJourneyStageLabel(stage: string | null | undefined): string | null {
  if (!stage?.trim()) return null;
  const trimmed = stage.trim();
  const withoutPrefix = trimmed.replace(/^\d+[a-z]?\.\s*/i, "");
  const label = withoutPrefix || trimmed;
  return label.length > 36 ? `${label.slice(0, 33)}…` : label;
}

/** Compact label for list rows (first segment before dash). */
export function compactJourneyStageLabel(stage: string | null | undefined): string | null {
  const short = shortJourneyStageLabel(stage);
  if (!short) return null;
  const head = short.split(" - ")[0]?.trim();
  return head || short;
}

/** Match Pulse journey stage groups (mirrors API ``stage_filters``). */
export function matchesClientStageGroup(
  journeyStage: string | null | undefined,
  group: ClientStageGroup | undefined,
): boolean {
  if (!group || !journeyStage?.trim()) return false;
  const lower = journeyStage.toLowerCase();
  return STAGE_GROUP_CONTAINS[group].some((needle) => lower.includes(needle));
}

/** Badge colour hint from journey stage text. */
export function journeyStageBadgeColor(
  stage: string | null | undefined,
): "indigo" | "amber" | "emerald" | "zinc" {
  const lower = (stage ?? "").toLowerCase();
  if (lower.includes("in dg") || lower.includes("awaiting dg") || lower.includes("ani tech")) {
    return "indigo";
  }
  if (lower.includes("paraplanning") || lower.includes("in advice")) {
    return "amber";
  }
  if (lower.includes("advised")) {
    return "emerald";
  }
  return "zinc";
}

/** Left accent for list rows by journey stage. */
export function journeyStageRowAccentClass(stage: string | null | undefined): string {
  switch (journeyStageBadgeColor(stage)) {
    case "indigo":
      return "border-l-indigo-500/70";
    case "amber":
      return "border-l-amber-500/70";
    case "emerald":
      return "border-l-emerald-500/70";
    default:
      return "border-l-slate-300/40 dark:border-l-slate-600/50";
  }
}

/** StatusDot colour for journey stage. */
export function journeyStageStatusDotColor(
  stage: string | null | undefined,
): "indigo" | "amber" | "emerald" | "gray" {
  switch (journeyStageBadgeColor(stage)) {
    case "indigo":
      return "indigo";
    case "amber":
      return "amber";
    case "emerald":
      return "emerald";
    default:
      return "gray";
  }
}
